import {
  Panel,
  SpacingVertical,
  Text,
  Title,
  DataListHeader,
  DataListRow,
  Grid,
  SkeletonLoader,
  GridCell,
  DataListExtraColumn,
  PaginationBar,
  ModalConfirm,
} from "mailup-web-components";
import {
  TABLE_FILES_EXTRA_COLUMN_AFTER_WIDTH,
  TABLE_FILES_EXTRA_COLUMN_BEFORE_WIDTH,
} from "Pages/ImportWizard/model";
import {
  modalDelesteImpostsState,
  tableState,
} from "Pages/ImportWizard/recoil/state/stateStep1";
import React, { useEffect, type FC } from "react";
import { useIntl } from "react-intl";
import {
  useRecoilCallback,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import FileListRow from "./FileListRow";
import {
  deleteFromListCallback,
  getImportFilesCallback,
} from "Pages/ImportWizard/recoil/callbacks";

const FileList: FC = () => {
  const intl = useIntl();
  const {
    orderBy,
    orderDescending,
    items,
    pageSize,
    pageNumber,
    loading,
    totalItems,
  } = useRecoilValue(tableState);
  const { show, fileName } = useRecoilValue(modalDelesteImpostsState);
  const resetModalDelete = useResetRecoilState(modalDelesteImpostsState);

  const setTableState = useSetRecoilState(tableState);
  const getImportFiles = useRecoilCallback(getImportFilesCallback, []);

  const deleteFromList = useRecoilCallback(deleteFromListCallback, []);

  useEffect(() => {
    void getImportFiles(false);
  }, [getImportFiles]);

  const handleClickSort = (val: string) => {
    setTableState((prev) => ({
      ...prev,
      orderBy: val,
      orderDescending: !orderDescending,
      pageNumber: 1,
    }));
    getImportFiles(false);
  };

  function handleChangePage(val: number) {
    setTableState((prev) => ({
      ...prev,
      pageNumber: val,
    }));
    getImportFiles(false);
  }

  function handleChangePageSize(val: number) {
    setTableState((prev) => ({
      ...prev,
      pageSize: val,
    }));
    getImportFiles(false);
  }

  const heads = [
    {
      field: "Name",
      label: intl.formatMessage({
        id: "Load.FileImport.FileTableHeaderName",
      }),
      span: 4,
      sortable: true,
    },
    {
      field: "Extension",
      label: intl.formatMessage({
        id: "Load.FileImport.FileTableHeaderFormat",
      }),
      span: 2,
      sortable: false,
    },
    {
      field: "CreationTime",
      label: intl.formatMessage({
        id: "Load.FileImport.FileTableHeaderDate",
      }),
      span: 3,
      sortable: true,
    },
    {
      field: "Size",
      label: intl.formatMessage({
        id: "Load.FileImport.FileTableHeaderSize",
      }),
      span: 3,
      sortable: false,
    },
  ];

  function onClickConfirm() {
    deleteFromList(fileName);
  }

  return (
    <>
      <ModalConfirm
        show={show}
        title={intl.formatMessage(
          {
            id: "Load.FileImportStatus.ModalDeleteSingleTitle",
          },
          {
            NAME: fileName,
          },
        )}
        message={intl.formatMessage({
          id: "Load.FileImportStatus.ModalDeleteDescr",
        })}
        stringConfirm={intl.formatMessage({
          id: "Load.MailupCommon.Remove",
        })}
        stringCancel={intl.formatMessage({
          id: "Load.MailupCommon.Cancel",
        })}
        variant="delete"
        onClickConfirm={onClickConfirm}
        onClickCancel={resetModalDelete}
      />
      <Panel>
        <Title variant="M">
          {intl.formatMessage({ id: "Load.FileImport.FileTableTitle" })}
        </Title>
        <SpacingVertical variant="16" />
        <Text>
          {intl.formatMessage({ id: "Load.FileImport.FileTableDescr" })}
        </Text>
        <SpacingVertical />
        <DataListHeader
          heads={heads}
          afterWidth={TABLE_FILES_EXTRA_COLUMN_AFTER_WIDTH}
          beforeWidth={TABLE_FILES_EXTRA_COLUMN_BEFORE_WIDTH}
          orderByStatus={orderBy}
          orderDescendingStatus={orderDescending}
          handleChangeColumnsOrderCallback={handleClickSort}
        />
        <div>
          {loading
            ? Array.from({
                length: totalItems < 5 ? totalItems : pageSize,
              }).map((_, i) => (
                <DataListRow key={`skeleton-${i}`}>
                  <DataListExtraColumn
                    before
                    width={TABLE_FILES_EXTRA_COLUMN_BEFORE_WIDTH}
                  />
                  <Grid cols={12}>
                    <GridCell span={4}>
                      <SkeletonLoader />
                    </GridCell>
                    <GridCell span={2}>
                      <SkeletonLoader />
                    </GridCell>
                    <GridCell span={3}>
                      <SkeletonLoader />
                    </GridCell>
                    <GridCell span={3}>
                      <SkeletonLoader />
                    </GridCell>
                  </Grid>
                  <DataListExtraColumn
                    after
                    width={TABLE_FILES_EXTRA_COLUMN_AFTER_WIDTH}
                  />
                </DataListRow>
              ))
            : items.map((item, i) => <FileListRow key={i} item={item} />)}
        </div>
        <PaginationBar
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalItems={totalItems}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
        />
      </Panel>
    </>
  );
};
export default FileList;
